import { ScrollStrategyOptions } from "@angular/cdk/overlay";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { UntypedFormArray } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { FormDataOptions } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import {
	Ts1SelectManeuverModalComponent,
	Ts1SelectManeuverModalData,
} from "../ts1-select-maneuver-modal/ts1-select-maneuver-modal.component";

@Component({
	selector: "app-ts1-assessment-config",
	templateUrl: "./ts1-assessment-config.component.html",
	styleUrls: ["./ts1-assessment-config.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1AssessmentConfigComponent {
	@Input() maneuvers!: UntypedFormArray;
	@Input() maneuverOptions: FormDataOptions[] = [];
	@Input() formTypeCode: string | null = null;
	@Input() maneuverAction: "create-form" | "edit-form" = "create-form";
	@Input() readOnly = false;
	@Output() addManeuver = new EventEmitter<FormDataOptions>();
	@Output() requestRemoveManeuver = new EventEmitter<number>();

	activeIndex = 0;

	constructor(
		private dialog: MatDialog,
		private scrollStrategies: ScrollStrategyOptions,
	) {}

	get usedManeuverCodes(): string[] {
		return this.maneuvers.controls
			.map((c) => {
				const v = c.get("maneuver")?.value;
				if (!v) return "";
				return typeof v === "string" ? v : (v as FormDataOptions).code;
			})
			.filter(Boolean);
	}

	onAddManeuverClick(): void {
		if (this.readOnly) return;
		this.openAddManeuverModal();
	}

	openAddManeuverModal(): void {
		const scrollY = window.scrollY;

		const dialogRef = this.dialog.open(Ts1SelectManeuverModalComponent, {
			panelClass: ["ts1-select-maneuver-modal-panel", "ts1-select-maneuver-modal"],
			width: "480px",
			autoFocus: false,
			restoreFocus: false,
			hasBackdrop: true,
			disableClose: false,
			scrollStrategy: this.scrollStrategies.block(),
			data: {
				options: this.maneuverOptions ?? [],
				usedCodes: this.usedManeuverCodes,
			} satisfies Ts1SelectManeuverModalData,
		});

		requestAnimationFrame(() => {
			window.scrollTo(0, scrollY);
		});

		dialogRef.afterClosed().subscribe((selected: FormDataOptions | null) => {
			requestAnimationFrame(() => {
				window.scrollTo(0, scrollY);
			});

			if (selected) {
				this.addManeuver.emit(selected);
				this.activeIndex = Math.max(0, this.maneuvers.length - 1);
			}
		});
	}

	onRemove(index: number): void {
		if (this.readOnly) return;
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "remove-maneuver-modal",
			height: "240px",
			width: "360px",
			autoFocus: false,
			restoreFocus: false,
			hasBackdrop: true,
			scrollStrategy: this.scrollStrategies.block(),
			data: {
				modalTitle: "Confirmation",
				modalContent: "You are about to delete this Maneuver and all associated OBs. Are you sure you want to proceed?",
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		dialogRef.afterClosed().subscribe((result: { close?: boolean }) => {
			if (result?.close === true) {
				this.requestRemoveManeuver.emit(index);
				if (this.activeIndex >= this.maneuvers.length) {
					this.activeIndex = Math.max(0, this.maneuvers.length - 1);
				}
			}
		});
	}
}
